import React, { useEffect, useRef, RefObject, useState, FC } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, PaperProps, IconButton } from '@mui/material'

import { useTranslation } from 'react-i18next'
import { AuthService } from 'services/AuthService'
import { useSnackbar } from 'notistack'
import Draggable from 'react-draggable'
import ListTransfer from 'components/transfer/ListTransfer'
import CloseIcon from '@mui/icons-material/Close'

interface AssignPermissionsProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    select?: any;
    reloading: () => void;
    permissions?: any;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const AssignPermissions: FC<AssignPermissionsProps> = ({ open, setOpen, select, reloading, permissions }) => {
    const { t } = useTranslation()
    const [elementos, setElementos] = useState([]);
    const [selectedElements, setSelectedPermissions] = useState([]);
    const { enqueueSnackbar } = useSnackbar()
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open) return;

        try {
            if (select.length > 0) {
                const normalizedPermissions = permissions.map((perm: any) => ({
                    ...perm,
                    id: perm.id || perm.permission_id // Usar id si existe, sino permission_id
                }));
                setElementos(normalizedPermissions)
                const normalizedSelectedPermissions = select[0]?.permissions.map((perm: any) => ({
                    ...perm,
                    id: perm.id || perm.permission_id
                }));
                setSelectedPermissions(normalizedSelectedPermissions)
            }
        } catch (error) {
            console.error('Error fetching permissions:', error)
        }
    }, [open, select, permissions])

    const AddAssignPermissions = async () => {
        const authService = new AuthService()
        const accessToken = localStorage.getItem('access_token')
        if (!accessToken) {
            console.error("No access token found in localStorage")
            return
        }

        try {
            setLoading(true)
            await authService.profile.assignPermission({
                profile_id: select[0].profile_id,
                permission_ids: selectedElements.map((item: Item) => item.id),
            })
            enqueueSnackbar(t('profiles.table.dialog.assign_permissions.messages.success'), { variant: 'success' })
            reloading()
            setOpen(false)
        } catch (error) {
            console.error("Error:", error)
            enqueueSnackbar(t('profiles.table.dialog.assign_permissions.messages.error'), { variant: 'error' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('profiles.table.dialog.assign_permissions.title')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => setOpen(false)}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <ListTransfer
                    loading={loading}
                    allItems={elementos}
                    right={selectedElements}
                    setRight={setSelectedPermissions}
                    textLeft={t('profiles.table.dialog.assign_permissions.available')}
                    textRight={t('profiles.table.dialog.assign_permissions.assigned')}
                    textSelected={t('general.transfer_list.selected')}
                />
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={() => setOpen(false)} color="error">
                    {t('profiles.table.dialog.assign_permissions.buttons.cancel')}
                </Button>
                <Button variant='contained' onClick={() => AddAssignPermissions()} color="primary" loading={loading}>
                    {t('profiles.table.dialog.assign_permissions.buttons.assign')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export { AssignPermissions }